import React, { useEffect, useState } from 'react';
import { Button } from '../../shared/components/Button';
import { useAnalytics } from '../../shared/hooks/useAnalytics';
import type { CrossPlatformOffering, CrossPlatformPackage, UseRevenueCatErrors } from '../../shared/hooks/useRevenueCat';
import { PaywallPricingCard } from './components/PaywallPricingCard';

interface PaywallPaymentDrawerProps {
    currentOffering?: CrossPlatformOffering | null;
    selectedPackage?: CrossPlatformPackage | null;
    referralCode?: string;
    onPackageSelect?: (packageToSelect: CrossPlatformPackage) => void;
    onSubscribe?: (selectedPackage: CrossPlatformPackage) => void;
    isLoading?: boolean;
    isPurchasing?: boolean;
    error?: UseRevenueCatErrors | null;
    onErrorDismiss?: () => void;
    paywallShowPerMonthMode?: boolean;
}

const PaywallPaymentDrawer: React.FC<PaywallPaymentDrawerProps> = ({
    currentOffering,
    selectedPackage,
    referralCode,
    onPackageSelect,
    onSubscribe,
    isLoading = false,
    isPurchasing = false,
    error,
    onErrorDismiss,
    paywallShowPerMonthMode = false
}) => {
    const [localSelectedPackage, setLocalSelectedPackage] = useState<CrossPlatformPackage | null>(null);
    const { logEvent } = useAnalytics();
    // Use either controlled selectedPackage prop or local state
    const effectiveSelectedPackage = selectedPackage || localSelectedPackage;

    // Auto-select the first package when offerings load (if no package is selected)
    useEffect(() => {
        if (currentOffering && !effectiveSelectedPackage) {
            handlePackageSelect(currentOffering.annual);
        }
    }, [currentOffering, effectiveSelectedPackage, onPackageSelect]);

    const handlePackageSelect = (pkg: CrossPlatformPackage) => {
        logEvent("paywall_package_selected", { package_id: pkg.originalPackage.identifier });
        setLocalSelectedPackage(pkg);
        onPackageSelect?.(pkg);
    };

    const handleSubscribe = () => {
        if (effectiveSelectedPackage) {
            logEvent("paywall_purchase_attempted", { package_id: effectiveSelectedPackage.originalPackage.identifier });
            onSubscribe?.(effectiveSelectedPackage);
        }
    };

    const handleErrorDismiss = () => {
        logEvent("paywall_error_dismissed");
        onErrorDismiss?.();
    };

    // Show error state if there's an error
    if (error?.purchase || error?.restore || error?.offerings || error?.customerInfo) {
        return (
            <div className="relative flex-shrink-0 w-full bg-white rounded-t-[39px] px-8 pb-4 pt-6 shadow-[0_-10px_25px_-3px_rgba(0,0,0,0.1),0_-4px_6px_-2px_rgba(0,0,0,0.05)]">
                <div className="flex flex-col justify-center items-center py-8 gap-4">
                    <div className='text-osDanger-1 text-center text-lg font-semibold'>Error</div>
                    <div className="text-osBlack text-center">{error.purchase ?? error.restore ?? error.offerings ?? error.customerInfo}</div>
                    <Button
                        variant="black"
                        className='w-full'
                        label="Dismiss"
                        onClick={handleErrorDismiss}
                    />
                </div>
            </div>
        );
    }

    // Show loading state if offerings are loading
    if (isLoading || !currentOffering || !effectiveSelectedPackage) {
        return (
            <div className="relative flex-shrink-0 w-full bg-white rounded-t-[39px] px-8 pb-4 pt-6 shadow-[0_-10px_25px_-3px_rgba(0,0,0,0.1),0_-4px_6px_-2px_rgba(0,0,0,0.05)]">
                <div className="flex justify-center items-center py-8">
                    <div className="text-osGrey-2">Loading subscription plans...</div>
                </div>
            </div>
        );
    }

    const finePrint = effectiveSelectedPackage.hasTrial ?
        <><span className='font-semibold text-osBlack'>7 days free, then billed {effectiveSelectedPackage.billingPeriod} at {effectiveSelectedPackage.formattedPrice}.</span><br /><span className='font-semibold text-osBlack underline'>Cancel 24 hours before trial ends.</span></> :
        <><span>Start your plan today,</span><span className='font-semibold text-osBlack'> billed {effectiveSelectedPackage.billingPeriod} <br /> starting today at {effectiveSelectedPackage.formattedPrice}</span></>;

    const buttonLabel = effectiveSelectedPackage.hasTrial ? "Start my 7-Day Free Trial" : "Start my Journey";

    const primaryPackage = currentOffering.annual;

    const secondaryPackage = currentOffering.monthly;

    const shouldShowDrawerBadge = effectiveSelectedPackage.drawerBadgeText;
    const drawerBadgeText = referralCode ? `${referralCode} - ${effectiveSelectedPackage.drawerBadgeText}` : effectiveSelectedPackage.drawerBadgeText;

    return (
        <div className="relative flex-shrink-0 w-full bg-white rounded-t-[48px] px-8 pt-12 shadow-[0_-10px_25px_-3px_rgba(0,0,0,0.1),0_-4px_6px_-2px_rgba(0,0,0,0.05)]">
            {shouldShowDrawerBadge && <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                <div className="bg-pink-mascotte text-osBlack text-sm font-semibold px-4 py-2 rounded-full whitespace-nowrap">
                    {drawerBadgeText}
                </div>
            </div>}
            <div className="flex justify-center gap-4 mt-2 mb-4">
                <PaywallPricingCard
                    paywallShowPerMonthMode={paywallShowPerMonthMode}
                    pricingPackage={secondaryPackage}
                    isSelected={effectiveSelectedPackage === secondaryPackage}
                    onClick={() => handlePackageSelect(secondaryPackage)}
                />
                <PaywallPricingCard
                    paywallShowPerMonthMode={paywallShowPerMonthMode}
                    pricingPackage={primaryPackage}
                    isSelected={effectiveSelectedPackage === primaryPackage}
                    onClick={() => handlePackageSelect(primaryPackage)}
                />
            </div>
            <p className='text-center text-osGrey-2 text-xs mb-1 text-balance px-4'>
                {finePrint}
            </p>
            <Button
                variant="black"
                className='w-full'
                label={isPurchasing ? "Processing..." : buttonLabel}
                inactive={isPurchasing || !effectiveSelectedPackage}
                isLoading={isPurchasing}
                onClick={handleSubscribe}
            />
            <p className='text-center text-osBlack font-semibold text-sm mt-[10px] safe-pb-4'>Cancel Anytime</p>
        </div>
    );
};

export default PaywallPaymentDrawer;
